import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { usePermissionCheck } from '../../contexts/PermissionContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import apiService from '../../services/api'
import {
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineMagnifyingGlass,
  HiOutlineFolder,
  HiOutlineFolderOpen,
  HiOutlineDocumentText,
  HiOutlineChevronRight,
  HiOutlineXMark,
} from 'react-icons/hi2'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import DocumentGroupModal from '../../components/documents/DocumentGroupModal'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  IconButton,
  Input,
  PageHeader,
} from '../../components/ui'

export default function DocumentGroupsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, isWorkspaceSwitched, ownerAccountId } = useAuth()
  const { showSuccess, showError } = useAlerts()
  const { canCreate, canDelete } = usePermissionCheck()
  const { getEntity } = useWorkspace()

  const [documentGroups, setDocumentGroups] = useState([])
  const [groupsLoading, setGroupsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showGroupModal, setShowGroupModal] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false, title: '', message: '', onConfirm: null, type: 'warning',
  })

  const permissions = {
    canCreateGroup: canCreate('document_group'),
    canDeleteGroup: canDelete('document_group'),
    isOwner: !isWorkspaceSwitched,
  }

  useEffect(() => {
    fetchDocumentGroups()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWorkspaceSwitched, searchQuery])

  async function fetchDocumentGroups() {
    try {
      setGroupsLoading(true)
      const params = {}
      if (isWorkspaceSwitched && ownerAccountId) params.owner_account_id = ownerAccountId
      const response = await apiService.get('/document-groups/', { params })
      if (response.success) {
        let arr = response.data.data || response.data.results || response.data || []
        if (searchQuery) {
          const q = searchQuery.toLowerCase()
          arr = arr.filter(g =>
            g.name.toLowerCase().includes(q) ||
            (g.description && g.description.toLowerCase().includes(q)),
          )
        }
        setDocumentGroups(arr)
      }
    } catch { /* swallow */ } finally {
      setGroupsLoading(false)
    }
  }

  async function handleDeleteGroup(groupId) {
    if (!permissions.canDeleteGroup) {
      showError(t('common.permissionDenied'))
      return
    }
    try {
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.delete(`/document-groups/${groupId}/`, { params })
      if (response.success) {
        showSuccess(t('documents.deleteSuccess', { entity: `${getEntity('document').singular} group` }))
        fetchDocumentGroups()
      } else {
        showError(response.error?.message || t('documents.deletedFailed', { entity: `${getEntity('document').singular.toLowerCase()} group` }))
      }
    } catch {
      showError(t('documents.deletedFailed', { entity: `${getEntity('document').singular.toLowerCase()} group` }))
    }
  }

  const confirmDeleteGroup = (group) => {
    const docCount = group.document_count || 0
    setConfirmDialog({
      isOpen: true,
      title: t('documents.groups.deleteTitle', { name: group.name }),
      message: docCount > 0
        ? t('documents.groups.deleteWithDocs', {
            count: docCount,
            entity: docCount === 1 ? getEntity('document').singular.toLowerCase() : getEntity('document').plural.toLowerCase(),
          })
        : t('documents.groups.deleteEmpty'),
      type: 'danger',
      confirmText: t('documents.groups.deleteConfirm'),
      onConfirm: () => {
        handleDeleteGroup(group.id)
        setConfirmDialog(prev => ({ ...prev, isOpen: false }))
      },
    })
  }

  if (groupsLoading && documentGroups.length === 0) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <div className="h-6 w-1/3 bg-muted rounded animate-pulse" />
          <div className="h-4 w-1/2 bg-muted rounded animate-pulse" />
        </div>
        <Card padding="md">
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={HiOutlineFolder}
        iconTone="info"
        title={t('documents.groups.title', { entity: getEntity('document').singular })}
        subtitle={
          isWorkspaceSwitched
            ? t('documents.groups.subtitleSwitched', { name: user?.switchedAccountName })
            : t('documents.groups.subtitle', { entity: getEntity('document').plural.toLowerCase() })
        }
        actions={
          permissions.canCreateGroup && (
            <Button leadingIcon={HiOutlinePlus} onClick={() => setShowGroupModal(true)}>
              {t('documents.groups.newGroup')}
            </Button>
          )
        }
      />

      <Card padding="none">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 p-4 sm:p-5 border-b border-border-subtle">
          <div className="flex-1 max-w-lg">
            <Input
              leadingIcon={HiOutlineMagnifyingGlass}
              placeholder={t('documents.groups.searchPlaceholder', { entity: getEntity('document').singular.toLowerCase() })}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              trailingAction={
                searchQuery ? (
                  <IconButton
                    icon={HiOutlineXMark}
                    aria-label={t('common.clearSearch')}
                    variant="ghost"
                    size="sm"
                    onClick={() => setSearchQuery('')}
                  />
                ) : null
              }
            />
          </div>
          <p className="text-xs text-ink-muted">
            {t('documents.groups.groupCount', { count: documentGroups.length })}
          </p>
        </div>

        {documentGroups.length === 0 ? (
          <EmptyState
            icon={HiOutlineFolder}
            title={t('documents.groups.noFound', { entity: getEntity('document').singular.toLowerCase() })}
            description={t('documents.groups.noFoundDescription', { entity: getEntity('session').singular.toLowerCase() })}
            action={
              permissions.canCreateGroup && (
                <Button leadingIcon={HiOutlinePlus} onClick={() => setShowGroupModal(true)}>
                  {t('documents.groups.createFirst')}
                </Button>
              )
            }
          />
        ) : (
          <ul className="divide-y divide-border-subtle">
            {documentGroups.map((group) => {
              const docCount = group.document_count || 0
              return (
                <li
                  key={group.id}
                  className="flex items-center gap-3 px-4 sm:px-5 py-3.5 hover:bg-muted/40 transition-colors cursor-pointer group"
                  onClick={() => navigate(`/document-groups/${group.id}`)}
                >
                  <span className="w-10 h-10 bg-info-soft text-info rounded-xl flex items-center justify-center flex-shrink-0">
                    <HiOutlineFolderOpen className="w-5 h-5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-ink-primary group-hover:text-brand transition-colors truncate">
                      {group.name}
                    </h3>
                    <p className="text-xs text-ink-muted truncate">
                      {group.description || '—'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge tone="info" size="sm">
                      <HiOutlineDocumentText className="w-3 h-3" />
                      {docCount} {docCount === 1 ? getEntity('document').singular.toLowerCase() : getEntity('document').plural.toLowerCase()}
                    </Badge>
                    {permissions.isOwner && permissions.canDeleteGroup && (
                      <IconButton
                        icon={HiOutlineTrash}
                        aria-label={t('common.delete')}
                        variant="danger"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          confirmDeleteGroup(group)
                        }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    )}
                    <HiOutlineChevronRight className="w-4 h-4 text-ink-muted group-hover:text-brand transition-colors rtl:rotate-180" />
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </Card>

      <DocumentGroupModal
        isOpen={showGroupModal}
        onClose={() => setShowGroupModal(false)}
        onSuccess={() => {
          fetchDocumentGroups()
          setShowGroupModal(false)
        }}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
      />
    </div>
  )
}
